import { AI_GUARD_DEFAULTS, BILLING_PERIODS, CUSTOM_ADJUSTMENT_MAX_HOURS, CUSTOM_ADJUSTMENTS_PER_YEAR, customAdjustmentsUsed, TRIAL_EXTEND_DEFAULT_DAYS, TRIAL_EXTEND_MAX_DAYS, extendedTrialUntil, FEATURES, PLANS, ROLES, daysBetween, featureAvailable, formatMoney, fromMinor, inTrial, planIncludes, toMinor, todayIso, type BillingPeriod, type Plan, type Pricing, type Role } from "@szerviz/core";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorText } from "../components/ErrorText";
import { OperatorStats } from "./OperatorStats";
import { SUGGESTED_TAGS, aiCostAlert, invoiceOf, oddUse, seatText } from "./operatorShared";
import { useApp } from "../context";
import { BILLING_STATUSES, type AiSettings, type BillingStatus, type OpActivity, type OpAiLimit, type OpAiUsage, type OpContract, type OpCustomRequest, type OpLogEntry, type OpTrialActivity, type OpMember, type OpSubscription, type OpWorkshop } from "../data";

export type WarningKey = "expired" | "expiresSoon" | "inactive" | "aiCost" | "terms";
export interface WorkshopWarning {
  key: WarningKey;
  /** red: act now; orange: look at it */
  level: "red" | "orange";
  days?: number;
}

/** The newest of the last work order change and the last sign in of a member. */
export const lastSeen = (w: Pick<OpWorkshop, "lastActivity" | "lastSignIn">) =>
  [w.lastActivity, w.lastSignIn].filter((x): x is string => !!x).sort().at(-1) ?? null;

/**
 * Warnings of one workshop, from its data: paid until within 7 days (or passed), no activity for
 * 14 days, real AI cost above the alert share of the fee, legal terms not accepted.
 */
export function workshopWarnings(w: OpWorkshop, ai: OpAiUsage | undefined, alertShare: number, today = todayIso()): WorkshopWarning[] {
  const out: WorkshopWarning[] = [];
  if (w.paidUntil && w.status !== "free") {
    const left = daysBetween(today, w.paidUntil);
    if (left < 0) out.push({ key: "expired", level: "red", days: -left });
    else if (left <= 7) out.push({ key: "expiresSoon", level: "orange", days: left });
  }
  const seen = lastSeen(w) ?? w.createdAt;
  const idle = seen ? daysBetween(seen.slice(0, 10), today) : 0;
  if (idle >= 14) out.push({ key: "inactive", level: "orange", days: idle });
  if (aiCostAlert(ai, w.monthlyFee, alertShare)) out.push({ key: "aiCost", level: "orange" });
  if (!w.termsAccepted) out.push({ key: "terms", level: "red" });
  return out;
}

/** The fee after the discount (minor units). */
export const discountedFee = (fee: number | null, pct: number | null) => (fee === null ? null : Math.round(fee * (1 - (pct ?? 0) / 100)));

const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ block: "start" });

/**
 * One workshop for the operator: a strip with the key facts and warnings, then two columns with
 * users, package and options, subscription, AI, tags and note, contracts and the log; its own
 * statistics at the bottom. Dense, small controls (operator side rule).
 */
export function OperatorWorkshop({ w, activity, ai, aiSettings, aiLimit, sub, pricing, onChanged, onBack }: {
  w: OpWorkshop; activity: OpActivity | undefined; ai: OpAiUsage | undefined; aiSettings: AiSettings | null; aiLimit?: OpAiLimit;
  sub?: OpSubscription; pricing?: Pricing | null; onChanged: () => Promise<void>; onBack: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { store } = useApp();
  const alertShare = aiSettings?.alertShare ?? 0.3;
  const [members, setMembers] = useState<OpMember[]>([]);
  const [log, setLog] = useState<OpLogEntry[]>([]);
  const [contracts, setContracts] = useState<OpContract[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const money = (minor: number) => formatMoney(minor, "HUF", i18n.language);
  const date = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(i18n.language) : "–");
  const dateTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString(i18n.language, { dateStyle: "short", timeStyle: "short" }) : "–");

  const loadDetails = useCallback(async () => {
    setMembers(await store.opMemberList(w.id));
    setLog(await store.opLog(w.id));
    setContracts(await store.opContracts(w.id));
  }, [store, w.id]);
  useEffect(() => void loadDetails(), [loadDetails]);

  const run = async (fn: () => Promise<unknown>, ok = t("operator.saved")) => {
    setError(null);
    setMessage(null);
    try {
      await fn();
      await onChanged();
      await loadDetails();
      setMessage(ok);
      return true;
    } catch (err) {
      setError(err);
      return false;
    }
  };
  const extend = () => void run(() => store.opBulk([w.id], { action: "extend", value: { months: 1 } }));
  const warnings = workshopWarnings(w, ai, alertShare);
  const owner = w.ownerEmail;

  return (
    <section className="operator opw">
      <nav className="op-bar" aria-label={t("operator.title")}>
        <a href="#operator" onClick={(e) => { e.preventDefault(); onBack(); }}>← {t("operator.backToList")}</a>
        <strong>{w.name}</strong>
        <span className="muted">{owner ?? "–"}</span>
      </nav>
      <h1 className="sr-only">{w.name}</h1>

      <div className="opw-strip" role="group" aria-label={t("operator.strip")}>
        <Fact label={t("operator.plan")}>{t(`plan.${w.plan}.label`)}</Fact>
        <Fact label={t("operator.col.status")}><span className={`badge billing-${w.status}`}>{t(`billing.status.${w.status}`)}</span></Fact>
        <Fact label={t("operator.paidUntil")}>{date(w.paidUntil)}</Fact>
        <Fact label={t("operator.fee")}>
          {w.monthlyFee === null ? "–" : money(discountedFee(w.monthlyFee, w.discountPct)!)}
          {w.discountPct ? <small className="muted"> (−{w.discountPct}%)</small> : null}
        </Fact>
        {sub?.trialUntil && <Fact label={t("operator.col.trial")}>{inTrial(sub, todayIso()) ? <span className="warn-text">{date(sub.trialUntil)}</span> : date(sub.trialUntil)}</Fact>}
        {sub?.founder && <Fact label={t("operator.col.founder")}>{date(sub.founderUntil)}</Fact>}
        {pricing && <Fact label={t("operator.col.invoice")}>{money(invoiceOf(w, sub, ai, pricing).gross)}</Fact>}
        <Fact label={t("operator.lastSeen")}>{date(lastSeen(w))}</Fact>
        <Fact label={t("operator.terms")}>{w.termsAccepted ? t("operator.termsOk") : <span className="warn-text">{t("operator.termsMissing")}</span>}</Fact>
        <Fact label={t("operator.col.users")}>{seatText(activity)}</Fact>
        <Fact label={t("operator.since")}>{date(w.createdAt)}</Fact>
        {w.tags.length > 0 && <Fact label={t("operator.col.tags")}>{w.tags.map((x) => <span key={x} className="op-tag">{x}</span>)}</Fact>}
      </div>
      {warnings.length > 0 && (
        <ul className="opw-warnings" aria-label={t("operator.warnings")}>
          {warnings.map((x) => (
            <li key={x.key} className={`opw-warn ${x.level}`}>
              <span>{t(`operator.warn.${x.key}`, { days: x.days ?? 0 })}</span>
              {(x.key === "expired" || x.key === "expiresSoon") && <button type="button" onClick={extend}>{t("operator.plusMonth")}</button>}
              {x.key === "aiCost" && <button type="button" onClick={() => scrollTo("opw-ai")}>{t("operator.warn.toAi")}</button>}
              {(x.key === "inactive" || x.key === "terms") && owner && <a className="button" href={`mailto:${owner}`}>{t("operator.warn.mail")}</a>}
            </li>
          ))}
        </ul>
      )}
      <ErrorText error={error} />
      {message && <p className="ok">{message}</p>}

      <div className="opw-grid">
        <div className="opw-col">
          <Members w={w} members={members} activity={activity} ai={ai} run={run} dateTime={dateTime} />
          <PackageCard w={w} run={run} />
        </div>
        <div className="opw-col">
          <Billing w={w} run={run} onExtend={extend} money={money} />
          {sub?.trialUntil && (inTrial(sub, todayIso()) || sub.readOnly) && <TrialExtendCard w={w} trialUntil={sub.trialUntil} run={run} date={date} dateTime={dateTime} />}
          {pricing && <SubscriptionCard w={w} sub={sub} ai={ai} pricing={pricing} run={run} money={money} date={date} />}
          <AiCard w={w} ai={ai} aiSettings={aiSettings} aiLimit={aiLimit} run={run} money={money} />
          <CustomRequestsCard w={w} plan={sub?.effectivePlan ?? w.plan} run={run} date={date} />
          <TagsCard w={w} run={run} />
          <section className="opw-card">
            <h2>{t("operator.contracts")}</h2>
            {contracts.length === 0 ? (
              <p className="warn-text">{t("operator.noContract")}</p>
            ) : (
              <ul className="opw-list">
                {contracts.map((c) => (
                  <li key={c.acceptedAt}>
                    {t("operator.contract", {
                      company: c.companyName, address: c.address, tax: c.taxNumber, reg: c.regNumber ?? "–", email: c.userEmail ?? "–",
                      version: c.version, at: new Date(c.acceptedAt).toLocaleString(i18n.language),
                    })}{" "}
                    {!c.confirmationSentAt && <span className="warn-text">{t("operator.confirmationMissing")}</span>}
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className="opw-card">
            <h2>{t("operator.log")}</h2>
            {log.length === 0 ? (
              <p className="muted">{t("operator.none")}</p>
            ) : (
              <ul className="opw-list opw-log">
                {log.map((l, i) => (
                  <li key={i}>
                    <span className="muted">{dateTime(l.at)}</span> · {t(`operator.action.${l.action}`, { defaultValue: l.action })}
                    {isBatch(l.details) && <span className="op-tag">{t("operator.batch")}</span>}
                    <small className="muted"> {l.adminEmail ?? "–"} · {logDetails(l.details)}</small>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
      <OperatorStats alertShare={alertShare} onOpen={() => undefined} workshopId={w.id} />
    </section>
  );
}

const isBatch = (d: unknown) => !!d && typeof d === "object" && "batch" in d && Number((d as { count?: unknown }).count ?? 1) > 1;
/** A short, readable form of a log entry's details (the batch id itself is left out). */
function logDetails(d: unknown): string {
  if (d === null || d === undefined) return "";
  if (typeof d !== "object" || Array.isArray(d)) return JSON.stringify(d);
  const { batch: _b, count: _c, ...rest } = d as Record<string, unknown>;
  return JSON.stringify(rest).slice(0, 160);
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="opw-fact">
      <span className="opw-fact-label">{label}</span>
      <span className="opw-fact-value">{children}</span>
    </div>
  );
}

type Run = (fn: () => Promise<unknown>, ok?: string) => Promise<boolean>;

function Members({ w, members, activity, ai, run, dateTime }: { w: OpWorkshop; members: OpMember[]; activity: OpActivity | undefined; ai: OpAiUsage | undefined; run: Run; dateTime: (iso: string | null) => string }) {
  const { t } = useTranslation();
  const { store } = useApp();
  const [seats, setSeats] = useState(activity && activity.seatLimit < 100000 ? String(activity.seatLimit) : "");
  const [removing, setRemoving] = useState<string | null>(null);
  const [resetting, setResetting] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const owners = members.filter((m) => m.role === "owner").length;
  return (
    <section className="opw-card" id="opw-users">
      <h2>{t("operator.members")}</h2>
      {note && <p className="ok" role="status">{note}</p>}
      <p className={oddUse(ai, activity) ? "warn-text" : "muted"}>
        {t("operator.activity", { orders: activity?.workOrders ?? 0, vehicles: activity?.vehicles ?? 0, users: seatText(activity) })}
      </p>
      <table className="opw-table opw-members">
        <thead>
          <tr><th>{t("operator.member.email")}</th><th>{t("operator.member.role")}</th><th>{t("operator.member.lastSignIn")}</th><th>{t("operator.member.mfa")}</th><th><span className="sr-only">{t("operator.member.actions")}</span></th></tr>
        </thead>
        <tbody>
          {members.map((m) => {
            const lastOwner = m.role === "owner" && owners <= 1;
            return (
              <tr key={m.userId}>
                <td>{m.email}</td>
                <td>
                  <span className="ops-group" role="radiogroup" aria-label={t("operator.member.roleOf", { email: m.email })}>
                    {ROLES.map((r) => (
                      <label key={r} className={`ops-chip${m.role === r ? " selected" : ""}`} title={lastOwner && r !== "owner" ? t("operator.member.lastOwner") : undefined}>
                        <input type="radio" name={`role-${m.userId}`} checked={m.role === r} disabled={lastOwner && r !== "owner"}
                          onChange={() => void run(() => store.opSetMemberRole(w.id, m.userId, r as Role))} />
                        {t(`role.${r}.label`)}
                      </label>
                    ))}
                  </span>
                </td>
                <td>{dateTime(m.lastSignIn)}</td>
                <td>{m.mfa === undefined ? "–" : m.mfa ? <span className="badge ok">{t("operator.member.mfaOn")}</span> : <span className="muted">{t("operator.member.mfaOff")}</span>}</td>
                <td className="opw-member-actions">
                  {m.mfa && (resetting === m.userId ? (
                    <span className="ops-confirm-inline" role="group" aria-label={t("operator.member.mfaResetConfirm", { email: m.email })}>
                      {t("operator.member.mfaResetConfirm", { email: m.email })}
                      <button type="button" className="danger" onClick={() => {
                        let mailed = false;
                        void run(async () => { mailed = (await store.opResetMfa(w.id, m.userId)).mailed; }, "")
                          .then((ok) => { setResetting(null); if (ok) setNote(t(mailed ? "operator.member.mfaResetDone" : "operator.member.mfaResetDoneNoMail", { email: m.email })); });
                      }}>{t("operator.member.mfaResetYes")}</button>
                      <button type="button" onClick={() => setResetting(null)}>{t("operator.bulk.cancel")}</button>
                    </span>
                  ) : (
                    <button type="button" onClick={() => setResetting(m.userId)}>{t("operator.member.mfaReset")}</button>
                  ))}
                  <button type="button" onClick={() => void run(() => store.opSendPasswordReset(w.id, m.email), t("operator.member.resetSent", { email: m.email }))}>
                    {t("operator.member.reset")}
                  </button>
                  {removing === m.userId ? (
                    <span className="ops-confirm-inline" role="group" aria-label={t("operator.member.removeConfirm", { email: m.email })}>
                      {t("operator.member.removeConfirm", { email: m.email })}
                      <button type="button" className="danger" onClick={() => void run(() => store.opRemoveMember(w.id, m.userId)).then(() => setRemoving(null))}>{t("operator.member.removeYes")}</button>
                      <button type="button" onClick={() => setRemoving(null)}>{t("operator.bulk.cancel")}</button>
                    </span>
                  ) : (
                    <button type="button" disabled={lastOwner} title={lastOwner ? t("operator.member.lastOwner") : undefined} onClick={() => setRemoving(m.userId)}>{t("operator.member.remove")}</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="opw-inline">
        <label className="field">
          <span>{t("operator.seats")}</span>
          <input id="op-seats" inputMode="numeric" value={seats} onChange={(e) => setSeats(e.target.value)} aria-label={t("operator.seats")} />
        </label>
        <button type="button" onClick={() => void run(() => store.opSetSeats(w.id, seats.trim() === "" ? null : Number(seats)))}>{t("operator.saveSeats")}</button>
      </div>
    </section>
  );
}

function PackageCard({ w, run }: { w: OpWorkshop; run: Run }) {
  const { t } = useTranslation();
  const { store } = useApp();
  const setPlan = (plan: Plan) => void run(() => store.opSetPlan(w.id, plan));
  const toggle = (key: string, on: boolean) => void run(() => store.opSetFeatures(w.id, on ? [...w.features, key] : w.features.filter((f) => f !== key)));
  const grant = (key: string, on: boolean) => void run(() => store.opSetFeatureGrants(w.id, on ? [...w.featureGrants, key] : w.featureGrants.filter((f) => f !== key)));
  return (
    <section className="opw-card" id="opw-package">
      <h2>{t("operator.packageTitle")}</h2>
      <fieldset className="ops-group opw-plans">
        <legend className="sr-only">{t("operator.plan")}</legend>
        {PLANS.map((p) => (
          <label key={p} className={`ops-chip${w.plan === p ? " selected" : ""}`}>
            <input type="radio" name="plan" checked={w.plan === p} onChange={() => setPlan(p)} />
            {t(`plan.${p}.label`)}
          </label>
        ))}
      </fieldset>
      <table className="opw-table opw-features">
        <thead>
          <tr><th>{t("operator.modules")}</th><th>{t("operator.feature.inPlan")}</th><th>{t("operator.feature.grant")}</th><th>{t("operator.feature.on")}</th></tr>
        </thead>
        <tbody>
          {FEATURES.map((f) => {
            const inPlan = planIncludes(w.plan, f.plan);
            const granted = w.featureGrants.includes(f.key);
            const available = featureAvailable(w.plan, f.key, w.featureGrants);
            const label = t(`feature.${f.key}.label`);
            return (
              <tr key={f.key} className={available ? undefined : "muted"}>
                <td>{label}</td>
                <td>{inPlan ? "✓" : t("features.inPlan", { plan: t(`plan.${f.plan}.label`) })}</td>
                <td>
                  {!inPlan && <input type="checkbox" checked={granted} onChange={(e) => grant(f.key, e.target.checked)} aria-label={t("operator.feature.grantOf", { feature: label })} />}
                </td>
                <td><input type="checkbox" checked={available && w.features.includes(f.key)} disabled={!available} aria-label={label} onChange={(e) => toggle(f.key, e.target.checked)} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function Billing({ w, run, onExtend, money }: { w: OpWorkshop; run: Run; onExtend: () => void; money: (minor: number) => string }) {
  const { t } = useTranslation();
  const { store } = useApp();
  const [status, setStatus] = useState<BillingStatus>(w.status);
  const [fee, setFee] = useState(w.monthlyFee === null ? "" : String(fromMinor(w.monthlyFee, "HUF")));
  const [discount, setDiscount] = useState(w.discountPct === null ? "" : String(w.discountPct));
  const [paidUntil, setPaidUntil] = useState(w.paidUntil ?? "");
  useEffect(() => setPaidUntil(w.paidUntil ?? ""), [w.paidUntil]);
  useEffect(() => setStatus(w.status), [w.status]);
  const num = (v: string) => Number(v.replace(",", ".").trim());
  const feeMinor = fee.trim() === "" ? null : toMinor(num(fee), "HUF");
  const pct = discount.trim() === "" ? null : num(discount);
  const bad: string[] = [];
  if (feeMinor !== null && !(feeMinor >= 0)) bad.push(t("operator.monthlyFee"));
  if (pct !== null && !(pct >= 0 && pct <= 100)) bad.push(t("operator.discount"));
  const save = () => void run(() => store.opUpdateBilling(w.id, { status, monthlyFee: feeMinor, discountPct: pct, paidUntil: paidUntil || null }));
  const net = bad.length === 0 ? discountedFee(feeMinor, pct) : null;
  return (
    <section className="opw-card" id="opw-billing">
      <h2>{t("operator.billing")}</h2>
      <fieldset className="ops-group">
        <legend className="sr-only">{t("operator.col.status")}</legend>
        {BILLING_STATUSES.map((s) => (
          <label key={s} className={`ops-chip${status === s ? " selected" : ""}`}>
            <input type="radio" name="billingStatus" checked={status === s} onChange={() => setStatus(s)} />
            {t(`billing.status.${s}`)}
          </label>
        ))}
      </fieldset>
      <div className="opw-inline">
        <label className="field">
          <span>{t("operator.monthlyFee")}</span>
          <input inputMode="decimal" value={fee} onChange={(e) => setFee(e.target.value)} aria-label={t("operator.monthlyFee")} />
        </label>
        <label className="field">
          <span>{t("operator.discount")}</span>
          <input inputMode="decimal" value={discount} onChange={(e) => setDiscount(e.target.value)} aria-label={t("operator.discount")} />
        </label>
        <label className="field">
          <span>{t("operator.paidUntil")}</span>
          <input type="date" value={paidUntil} onChange={(e) => setPaidUntil(e.target.value)} aria-label={t("operator.paidUntil")} />
        </label>
        <button type="button" onClick={onExtend}>{t("operator.plusMonth")}</button>
      </div>
      {net !== null && <p className="muted">{t("operator.feeAfterDiscount", { fee: money(net) })}</p>}
      <div className="actions">
        <button type="button" className="primary" disabled={bad.length > 0} onClick={save}>{t("operator.save")}</button>
        {bad.length > 0 && <span className="missing">{t("operator.invalid", { fields: bad.join(", ") })}</span>}
      </div>
      <p className="muted small">{t("operator.smsThisMonth", { count: w.smsCount, amount: money(w.smsAmount) })}</p>
    </section>
  );
}

function AiCard({ w, ai, aiSettings, aiLimit, run, money }: { w: OpWorkshop; ai: OpAiUsage | undefined; aiSettings: AiSettings | null; aiLimit?: OpAiLimit; run: Run; money: (minor: number) => string }) {
  const { t } = useTranslation();
  const { store } = useApp();
  const [allowance, setAllowance] = useState(w.aiAllowance === null ? "" : String(w.aiAllowance));
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [dailyCap, setDailyCap] = useState(aiLimit?.dailyCapHuf == null ? "" : String(aiLimit.dailyCapHuf));
  const [buyLimit, setBuyLimit] = useState(aiLimit?.purchaseLimitHuf == null ? "" : String(aiLimit.purchaseLimitHuf));
  const alertShare = aiSettings?.alertShare ?? 0.3;
  const planAllowance = aiSettings?.allowance[w.plan] ?? 0;
  const num = (v: string) => Number(v.replace(",", ".").trim());
  const allowanceBad = allowance.trim() !== "" && !(num(allowance) >= 0);
  const missing: string[] = [];
  if (!(num(amount) > 0)) missing.push(t("operator.gift.amount"));
  if (!reason.trim()) missing.push(t("operator.gift.reason"));
  const gift = async () => {
    const ok = await run(() => store.opBulk([w.id], { action: "gift", value: { amount: Math.round(num(amount)), reason: reason.trim() } }), t("operator.gift.done", { amount: money(Math.round(num(amount))) }));
    if (ok) { setAmount(""); setReason(""); }
  };
  return (
    <section className="opw-card" id="opw-ai">
      <h2>{t("operator.aiTitle")}</h2>
      <p className={aiCostAlert(ai, w.monthlyFee, alertShare) ? "warn-text" : undefined}>
        {t("operator.ai.thisMonth", {
          calls: ai?.calls ?? 0,
          cost: money(ai?.costHuf ?? 0),
          charged: money(ai?.chargedHuf ?? 0),
          shared: money(ai?.sharedCostHuf ?? 0),
          bought: money(ai?.boughtHuf ?? 0),
        })}
      </p>
      <p className="muted">{t("operator.gift.sum", { month: money(w.giftMonthHuf), total: money(w.giftTotalHuf) })}</p>
      <div className="opw-inline">
        <label className="field">
          <span>{t("operator.aiAllowance", { plan: money(planAllowance) })}</span>
          <input inputMode="numeric" value={allowance} onChange={(e) => setAllowance(e.target.value)} aria-label={t("operator.aiAllowanceLabel")} />
        </label>
        <button type="button" disabled={allowanceBad} onClick={() => void run(() => store.opUpdateBilling(w.id, { aiAllowance: allowance.trim() === "" ? null : Math.round(num(allowance)) }))}>{t("operator.aiAllowanceSave")}</button>
      </div>
      <p className="muted small">
        {t("operator.aiLimits.today", { cost: money(aiLimit?.todayCostHuf ?? 0), cap: money(aiLimit?.dailyCapHuf ?? aiSettings?.workshopDailyCapHuf ?? AI_GUARD_DEFAULTS.workshopDailyCapHuf) })}
        {" · "}
        {t("operator.aiLimits.bought", { bought: money(aiLimit?.monthBoughtHuf ?? 0), limit: money(aiLimit?.purchaseLimitHuf ?? aiSettings?.monthlyPurchaseLimitHuf ?? AI_GUARD_DEFAULTS.monthlyPurchaseLimitHuf) })}
      </p>
      <div className="opw-inline">
        <label className="field">
          <span>{t("operator.aiLimits.dailyCap", { platform: money(aiSettings?.workshopDailyCapHuf ?? AI_GUARD_DEFAULTS.workshopDailyCapHuf) })}</span>
          <input inputMode="numeric" value={dailyCap} onChange={(e) => setDailyCap(e.target.value)} aria-label={t("operator.aiLimits.dailyCapLabel")} />
        </label>
        <label className="field">
          <span>{t("operator.aiLimits.purchaseLimit", { platform: money(aiSettings?.monthlyPurchaseLimitHuf ?? AI_GUARD_DEFAULTS.monthlyPurchaseLimitHuf) })}</span>
          <input inputMode="numeric" value={buyLimit} onChange={(e) => setBuyLimit(e.target.value)} aria-label={t("operator.aiLimits.purchaseLimitLabel")} />
        </label>
        <button type="button" disabled={(dailyCap.trim() !== "" && !(num(dailyCap) >= 0)) || (buyLimit.trim() !== "" && !(num(buyLimit) >= 0))}
          onClick={() => void run(() => store.opUpdateBilling(w.id, {
            aiDailyCapHuf: dailyCap.trim() === "" ? null : Math.round(num(dailyCap)),
            aiPurchaseLimitHuf: buyLimit.trim() === "" ? null : Math.round(num(buyLimit)),
          }))}>{t("operator.aiLimits.save")}</button>
      </div>
      <div className="opw-inline">
        <label className="field">
          <span>{t("operator.gift.amount")}</span>
          <input inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} aria-label={t("operator.gift.amount")} />
        </label>
        <label className="field wide">
          <span>{t("operator.gift.reason")}</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} aria-label={t("operator.gift.reason")} />
        </label>
        <button type="button" disabled={missing.length > 0} onClick={() => void gift()}>{t("operator.gift.give")}</button>
      </div>
      {missing.length > 0 && (amount || reason) && <p className="missing">{t("operator.missing", { fields: missing.join(", ") })}</p>}
      <p className="muted small">{t("operator.gift.note")}</p>
    </section>
  );
}

function TagsCard({ w, run }: { w: OpWorkshop; run: Run }) {
  const { t } = useTranslation();
  const { store } = useApp();
  const [tag, setTag] = useState("");
  const [note, setNote] = useState(w.note ?? "");
  const setTags = (tags: string[]) => void run(() => store.opUpdateBilling(w.id, { tags }));
  const add = (x: string) => {
    const v = x.trim();
    if (!v || w.tags.includes(v)) return;
    setTags([...w.tags, v]);
    setTag("");
  };
  return (
    <section className="opw-card" id="opw-tags">
      <h2>{t("operator.tags.title")}</h2>
      <div className="ops-group">
        {w.tags.length === 0 && <span className="muted">{t("operator.tags.none")}</span>}
        {w.tags.map((x) => (
          <span key={x} className="op-tag">
            {x}
            <button type="button" className="op-tag-x" aria-label={t("operator.tags.remove", { tag: x })} onClick={() => setTags(w.tags.filter((y) => y !== x))}>×</button>
          </span>
        ))}
      </div>
      <div className="opw-inline">
        <label className="field">
          <span>{t("operator.tags.tag")}</span>
          <input value={tag} maxLength={30} onChange={(e) => setTag(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(tag); }} aria-label={t("operator.tags.tag")} />
        </label>
        <button type="button" disabled={!tag.trim()} onClick={() => add(tag)}>{t("operator.tags.add")}</button>
        {SUGGESTED_TAGS.filter((x) => !w.tags.includes(x)).map((x) => (
          <button key={x} type="button" className="op-tag-suggest" onClick={() => add(x)}>+ {x}</button>
        ))}
      </div>
      <label className="field wide">
        <span>{t("operator.note")}</span>
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} aria-label={t("operator.note")} />
      </label>
      <div className="actions">
        <button type="button" onClick={() => void run(() => store.opUpdateBilling(w.id, { note: note.trim() || null }))}>{t("operator.noteSave")}</button>
      </div>
    </section>
  );
}

/** Trial, founder offer, period, extra users and this month's invoice lines (packages/core monthlyCharge). */
function SubscriptionCard({ w, sub, ai, pricing, run, money, date }: {
  w: OpWorkshop; sub: OpSubscription | undefined; ai: OpAiUsage | undefined; pricing: Pricing; run: Run; money: (minor: number) => string; date: (iso: string | null) => string;
}) {
  const { t } = useTranslation();
  const { store } = useApp();
  const [trialUntil, setTrialUntil] = useState(sub?.trialUntil ?? "");
  const [founderUntil, setFounderUntil] = useState(sub?.founderUntil ?? "");
  const [extra, setExtra] = useState(String(sub?.extraSeats ?? 0));
  useEffect(() => { setTrialUntil(sub?.trialUntil ?? ""); setFounderUntil(sub?.founderUntil ?? ""); setExtra(String(sub?.extraSeats ?? 0)); }, [sub]);
  const c = invoiceOf(w, sub, ai, pricing);
  const extraN = Number(extra.trim());
  const extraBad = !Number.isInteger(extraN) || extraN < 0 || extraN > 1000;
  const update = (patch: Parameters<typeof store.opUpdateSubscription>[1]) => void run(() => store.opUpdateSubscription(w.id, patch));
  return (
    <section className="opw-card" id="opw-subscription">
      <h2>{t("operator.sub.title")}</h2>
      <p className="muted">
        {t("operator.sub.summary", {
          plan: t(`plan.${w.plan}.label`),
          effective: t(`plan.${sub?.effectivePlan ?? w.plan}.label`),
          chosen: sub?.planAfterTrial ? t(`plan.${sub.planAfterTrial}.label`) : t("operator.sub.notChosen"),
          anchor: date(sub?.billingAnchor ?? null),
        })}
      </p>
      {sub?.readOnly && <p className="warn-text">{t("operator.sub.readOnly", { date: date(sub.trialUntil) })}</p>}
      <div className="opw-inline">
        <label className="field">
          <span>{t("operator.sub.trialUntil")}</span>
          <input type="date" value={trialUntil} onChange={(e) => setTrialUntil(e.target.value)} aria-label={t("operator.sub.trialUntil")} />
        </label>
        <button type="button" onClick={() => update({ trialUntil: trialUntil || null })}>{t("operator.sub.saveTrial")}</button>
      </div>
      <fieldset className="ops-group">
        <legend className="sr-only">{t("operator.col.period")}</legend>
        {BILLING_PERIODS.map((p: BillingPeriod) => (
          <label key={p} className={`ops-chip${(sub?.period ?? "monthly") === p ? " selected" : ""}`}>
            <input type="radio" name="op-period" checked={(sub?.period ?? "monthly") === p} onChange={() => update({ period: p })} />
            {t(`subscription.periodShort.${p}`)}
          </label>
        ))}
      </fieldset>
      <div className="opw-inline">
        <label className="field">
          <span>{t("operator.sub.extraSeats")}</span>
          <input inputMode="numeric" value={extra} onChange={(e) => setExtra(e.target.value)} aria-label={t("operator.sub.extraSeats")} />
        </label>
        <button type="button" disabled={extraBad} onClick={() => update({ extraSeats: extraN })}>{t("operator.sub.saveExtra")}</button>
      </div>
      <div className="opw-inline">
        <label className="ops-chip">
          <input type="checkbox" checked={!!sub?.founder} onChange={(e) => update({ founder: e.target.checked })} aria-label={t("operator.col.founder")} />
          {t("operator.col.founder")}
        </label>
        {sub?.founder && (
          <>
            <label className="field">
              <span>{t("operator.sub.founderUntil")}</span>
              <input type="date" value={founderUntil} onChange={(e) => setFounderUntil(e.target.value)} aria-label={t("operator.sub.founderUntil")} />
            </label>
            <button type="button" onClick={() => update({ founderUntil: founderUntil || null })}>{t("operator.sub.saveFounder")}</button>
            <small className="muted">{t("operator.sub.priceLock", { date: date(sub.priceLockUntil) })}</small>
          </>
        )}
      </div>
      <table className="opw-table">
        <caption className="muted">{c.periodStart ? t("operator.sub.invoiceFrom", { date: date(c.periodStart) }) : t("operator.sub.noPeriod")}</caption>
        <tbody>
          {c.lines.map((l) => (
            <tr key={l.kind}>
              <td>{t(`operator.sub.line.${l.kind}`, { count: l.quantity })}</td>
              <td className="num">{money(l.net)}{l.listNet > l.net && <small className="muted"> ({money(l.listNet)})</small>}</td>
              <td className="num">{money(l.vat)}</td>
              <td className="num">{money(l.gross)}</td>
            </tr>
          ))}
          <tr><th>{t("operator.sub.total")}</th><th className="num">{money(c.net)}</th><th className="num">{money(c.vat)}</th><th className="num">{money(c.gross)}</th></tr>
        </tbody>
      </table>
    </section>
  );
}

/**
 * Custom adjustments of the workshop (owner's decision, 2026-11-03): this year's used / included counter, the
 * list (every year) with done, cancel and reopen, and a small form to record one. Included ones are at most
 * 4 hours; when the allowance is used up only an individual offer can be recorded.
 */
function CustomRequestsCard({ w, plan, run, date }: { w: OpWorkshop; plan: Plan; run: Run; date: (iso: string | null) => string }) {
  const { t, i18n } = useTranslation();
  const { store } = useApp();
  const year = Number(todayIso().slice(0, 4));
  const [list, setList] = useState<OpCustomRequest[]>([]);
  const [title, setTitle] = useState("");
  const [hours, setHours] = useState("");
  const [included, setIncluded] = useState(true);
  const load = useCallback(async () => setList(await store.opCustomRequests(w.id)), [store, w.id]);
  useEffect(() => void load(), [load]);
  const allowance = CUSTOM_ADJUSTMENTS_PER_YEAR[plan];
  const used = customAdjustmentsUsed(list, year);
  const full = used >= allowance;
  const incl = included && !full;
  const hv = Number(hours.replace(",", ".").trim());
  const h = hours.trim() && Number.isFinite(hv) ? hv : null;
  const missing = [
    ...(title.trim().length < 3 ? [t("operator.custom.what")] : []),
    ...(h === null || h <= 0 || (incl && h > CUSTOM_ADJUSTMENT_MAX_HOURS) ? [t("operator.custom.hours")] : []),
  ];
  const act = (fn: () => Promise<unknown>) => run(async () => { await fn(); await load(); });
  const add = () => void act(() => store.opAddCustomRequest(w.id, { year, title: title.trim(), hours: h!, included: incl })).then((ok) => {
    if (ok) { setTitle(""); setHours(""); }
  });
  const num = (n: number) => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(n);
  return (
    <section className="opw-card" id="opw-custom">
      <h2>{t("operator.custom.title")}</h2>
      <p><strong>{t("operator.custom.used", { year, used, included: allowance })}</strong></p>
      {full && <p className="muted">{t("operator.custom.full")}</p>}
      {list.length === 0 ? (
        <p className="muted">{t("operator.none")}</p>
      ) : (
        <table className="opw-table">
          <thead><tr><th>{t("operator.custom.year")}</th><th>{t("operator.custom.what")}</th><th className="num">{t("operator.custom.hours")}</th><th>{t("operator.col.status")}</th><th /></tr></thead>
          <tbody>
            {list.map((r) => (
              <tr key={r.id}>
                <td>{r.year}</td>
                <td>{r.title}{!r.included && <span className="op-tag">{t("subscription.customOffer")}</span>}<br /><small className="muted">{date(r.createdAt)} · {r.createdByEmail ?? "–"}</small></td>
                <td className="num">{num(r.hours)}</td>
                <td>{t(`customRequest.status.${r.status}`)}</td>
                <td>
                  {r.status === "open" && <button type="button" onClick={() => void act(() => store.opUpdateCustomRequest(r.id, "done"))}>{t("operator.custom.done")}</button>}
                  {r.status !== "cancelled" && <button type="button" onClick={() => void act(() => store.opUpdateCustomRequest(r.id, "cancelled"))}>{t("operator.custom.cancel")}</button>}
                  {r.status !== "open" && <button type="button" onClick={() => void act(() => store.opUpdateCustomRequest(r.id, "open"))}>{t("operator.custom.reopen")}</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="opw-inline">
        <label className="field wide">
          <span>{t("operator.custom.what")}</span>
          <input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} aria-label={t("operator.custom.what")} />
        </label>
        <label className="field">
          <span>{t("operator.custom.hours")}</span>
          <input inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)} aria-label={t("operator.custom.hours")} />
        </label>
        <label className="ops-chip">
          <input type="checkbox" checked={incl} disabled={full} onChange={(e) => setIncluded(e.target.checked)} aria-label={t("operator.custom.included", { hours: CUSTOM_ADJUSTMENT_MAX_HOURS })} />
          {t("operator.custom.included", { hours: CUSTOM_ADJUSTMENT_MAX_HOURS })}
        </label>
        <button type="button" disabled={missing.length > 0} onClick={add}>{t("operator.custom.add")}</button>
        {missing.length > 0 && <small className="warn-text">{t("operator.missing", { fields: missing.join(", ") })}</small>}
      </div>
    </section>
  );
}

/**
 * Extending the trial (owner's decision, 2026-11-03): only in the trial or read only after it without a plan,
 * 1 to 90 days (default 30) from the later of the trial end and today. Next to it what the workshop did in the
 * last 7 days, so the operator sees it is active.
 */
function TrialExtendCard({ w, trialUntil, run, date, dateTime }: { w: OpWorkshop; trialUntil: string; run: Run; date: (iso: string | null) => string; dateTime: (iso: string | null) => string }) {
  const { t } = useTranslation();
  const { store } = useApp();
  const [days, setDays] = useState(String(TRIAL_EXTEND_DEFAULT_DAYS));
  const [activity, setActivity] = useState<OpTrialActivity | null>(null);
  useEffect(() => {
    let live = true;
    store.opTrialActivity(w.id).then((a) => live && setActivity(a), () => live && setActivity(null));
    return () => {
      live = false;
    };
  }, [store, w.id]);
  const n = Number(days.trim());
  const ok = /^\d+$/.test(days.trim()) && n >= 1 && n <= TRIAL_EXTEND_MAX_DAYS;
  const extend = () => void run(() => store.opExtendTrial(w.id, n), t("operator.trialExtend.done", { date: date(extendedTrialUntil(trialUntil, todayIso(), n)) }));
  return (
    <section className="opw-card" id="opw-trial">
      <h2>{t("operator.trialExtend.title")}</h2>
      {activity && (
        <p className="muted">
          {t("operator.trialExtend.activity", { workOrders: activity.workOrders, vehicles: activity.vehicles, signedIn: activity.signedIn })}
          <br />
          <small>{t("operator.trialExtend.last", { activity: dateTime(activity.lastActivity), signIn: dateTime(activity.lastSignIn) })}</small>
        </p>
      )}
      <div className="opw-inline">
        <label className="field">
          <span>{t("operator.trialExtend.days")}</span>
          <input inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value)} aria-label={t("operator.trialExtend.days")} />
        </label>
        <button type="button" disabled={!ok} onClick={extend}>{t("operator.trialExtend.title")}</button>
        {!ok && <small className="warn-text">{t("operator.trialExtend.invalid")}</small>}
      </div>
      <p className="small">{t("operator.sub.trialUntil")}: <strong>{date(trialUntil)}</strong></p>
    </section>
  );
}
